import React, { useEffect, useMemo } from 'react';
import { useLingui } from '@lingui/react/macro';
import { useTheme } from 'styled-components';

import { Flex, Heading } from '@rover/kibble/core';
import { AppliesToEnum } from '@rover/rsdk/src/apiClient/latest/model/appliesToEnum';
import { Highlight as HighlightItem } from '@rover/rsdk/src/apiClient/latest/model/highlight';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';

import { SitterProfileHighlightsShownEvent } from '../../analyticsEvents';
import { useRevampProfileContext } from '../../hooks/useRevampProfileContext';
import { getDedupedHighlights } from '../../utils/utils';

import Highlight from './components/Highlight';

const MAX_HIGHLIGHTS = 6;
const COLUMN_GAP = 56;
const HALF_COLUMN_GAP = COLUMN_GAP / 2;

const HighlightsSection = (): JSX.Element | null => {
  const { t } = useLingui();
  const theme = useTheme();
  const { sitterData, serviceType, viewMode } = useRevampProfileContext();
  const service = sitterData.services.find((s) => s.slug === serviceType);
  const isCatOnly = viewMode.petType === 'cat-only';

  const sortedHighlights = useMemo(() => {
    const isVisibleForCatOnly = (highlight: HighlightItem): boolean =>
      highlight.appliesTo !== AppliesToEnum.dog;

    const highlights = service
      ? [...sitterData.highlights, ...service.highlights]
      : [...sitterData.highlights];
    const visibleHighlights = isCatOnly
      ? highlights.filter(isVisibleForCatOnly)
      : getDedupedHighlights(highlights);

    return [...visibleHighlights].sort((a, b) => a.rank - b.rank).slice(0, MAX_HIGHLIGHTS);
  }, [sitterData.highlights, service, isCatOnly]);

  useEffect(() => {
    if (!serviceType || sortedHighlights.length === 0) return;
    emitAnalyticsEvent(
      new SitterProfileHighlightsShownEvent({
        highlightIds: sortedHighlights.map((h) => h.id),
        highlightCount: sortedHighlights.length,
        serviceSlug: serviceType,
      })
    );
  }, [sitterData.opk, serviceType, sortedHighlights]);

  if (sortedHighlights.length === 0) return null;

  return (
    <Flex
      flexDirection="column"
      gap="lg"
      borderBottom="1px solid"
      borderBottomColor={theme.colors.separator.primary}
      pb="8x"
      pt="6x"
    >
      <Heading size="400" webProps={{ responsive: false }} py="4x">{t`Highlights`}</Heading>
      <Flex
        flexDirection={['column', 'row']}
        flexWrap="wrap"
        sx={{ rowGap: '8px', columnGap: ['0px', `${COLUMN_GAP}px`] }}
      >
        {sortedHighlights.map((item: HighlightItem) => (
          <Flex
            key={`${item.title}-${item.rank}`}
            flex={['1 1 100%', `1 1 calc(50% - ${HALF_COLUMN_GAP}px)`]}
            py="1x"
          >
            <Highlight
              title={item.title}
              color={item.color}
              backgroundColor={item.backgroundColor}
              iconName={item.icon}
              descriptionTitle={item.descriptionTitle}
              description={item.description}
            />
          </Flex>
        ))}
      </Flex>
    </Flex>
  );
};
export default HighlightsSection;
